/**
 * Profile Management Utilities
 */

/**
 * Download user data as JSON
 * @param {Object} userData - Complete user data object
 * @param {string} email - User email for filename
 */
export const downloadUserData = (userData, email) => {
  const dataStr = JSON.stringify(userData, null, 2);
  const dataBlob = new Blob([dataStr], { type: 'application/json' });
  const url = window.URL.createObjectURL(dataBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `wealthvision-data-${email.replace('@', '-')}-${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {boolean} Whether email is valid
 */
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate name (at least 2 characters, letters and spaces only)
 * @param {string} name - Name to validate
 * @returns {boolean} Whether name is valid
 */
export const validateName = (name) => {
  if (!name || name.trim().length < 2) return false;
  const nameRegex = /^[a-zA-Z\s'-]+$/;
  return nameRegex.test(name.trim());
};

/**
 * Get subscription status color
 * @param {string} status - Subscription status
 * @returns {string} Tailwind color classes
 */
export const getSubscriptionStatusColor = (status) => {
  switch (status) {
    case 'active':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'cancelled':
      return 'bg-red-100 text-red-800 border-red-200';
    case 'expired':
      return 'bg-gray-100 text-gray-800 border-gray-200';
    case 'trialing':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

/**
 * Format subscription renewal date
 * @param {Date|string|Object} date - Renewal date (can be Date, string, or Firestore Timestamp)
 * @returns {string} Formatted date string
 */
export const formatRenewalDate = (date) => {
  if (!date) return 'N/A';

  let dateObj;

  // Handle Firestore Timestamp objects (they have a toDate() method)
  if (date && typeof date === 'object' && typeof date.toDate === 'function') {
    dateObj = date.toDate();
  }
  // Handle string dates
  else if (typeof date === 'string') {
    dateObj = new Date(date);
  }
  // Handle Date objects
  else if (date instanceof Date) {
    dateObj = date;
  }
  // Fallback
  else {
    return 'N/A';
  }

  // Validate that we have a valid date
  if (isNaN(dateObj.getTime())) {
    return 'N/A';
  }

  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Get analytics tracking preference from localStorage
 * @returns {boolean} Whether analytics is enabled
 */
export const getAnalyticsPreference = () => {
  const preference = localStorage.getItem('wealthvision-analytics');
  return preference !== 'false'; // Default to true if not set
};

/**
 * Set analytics tracking preference
 * @param {boolean} enabled - Whether to enable analytics
 */
export const setAnalyticsPreference = (enabled) => {
  localStorage.setItem('wealthvision-analytics', enabled.toString());
};

/**
 * Get auto-run AI analysis preference
 * @returns {boolean} Whether auto-run is enabled
 */
export const getAutoRunPreference = () => {
  const preference = localStorage.getItem('wealthvision-autorun-ai');
  return preference === 'true'; // Default to false
};

/**
 * Set auto-run AI analysis preference
 * @param {boolean} enabled - Whether to enable auto-run
 */
export const setAutoRunPreference = (enabled) => {
  localStorage.setItem('wealthvision-autorun-ai', enabled.toString());
};

/**
 * Prepare user data for export
 * @param {Object} userProfile - User profile data
 * @param {Array} investments - User investments
 * @param {Array} cryptocurrencies - User crypto holdings
 * @param {Array} accounts - User accounts
 * @returns {Object} Complete data export object
 */
export const prepareUserDataExport = (userProfile, investments, cryptocurrencies, accounts) => {
  return {
    exportDate: new Date().toISOString(),
    version: '1.0',
    profile: {
      email: userProfile?.email || '',
      name: userProfile?.name || '',
      country: userProfile?.country || 'US',
      plan: userProfile?.plan || 'free',
      createdAt: userProfile?.createdAt || null
    },
    investments: investments || [],
    cryptocurrencies: cryptocurrencies || [],
    accounts: accounts || [],
    settings: {
      theme: localStorage.getItem('wealthvision-theme') || 'light',
      analytics: getAnalyticsPreference(),
      autoRunAI: getAutoRunPreference()
    }
  };
};
